import {
  Component,
  ElementRef,
  inject,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import {
  MilParagraph,
  ParagraphComponent,
  ParagraphLayout,
  ParagraphMediaGallery,
} from '@madeinlune/massaud-types';
import { ParagraphSwitcherDirective } from '@madeinlune/paragraphs';
import { LetDirective, PushPipe } from '@ngrx/component';
import { DrupalIdentifier, Media } from '@madeinlune/drupal-types';
import { MediaSwitcherDirective } from '@nx-massaud/media/ui/switcher';
import { ComponentStore } from '@ngrx/component-store';
import { ResponsiveClassesDirective } from '@nx-massaud/responsive';
import { trackByProp } from '@nx-massaud/utils';
import { map } from 'rxjs';

interface LayoutComponentState {
  children: (MilParagraph | Media)[];
  itemClassNames: string[];
  classNames: string[];
}

@Component({
  selector: 'mil-layout',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    ParagraphSwitcherDirective,
    MediaSwitcherDirective,
    PushPipe,
    LetDirective,
    ResponsiveClassesDirective,
  ],
  template: `
    <!--<ng-container *ngIf="items">
      <div class="snap-controls">
        <button (click)="snapScroller.previous()">P</button>
        <span
          >{{ ((snapScroller.index$ | ngrxPush) ?? 0) + 1 }}/{{
            snapScroller.pageCount$ | ngrxPush
          }}</span
        >
        <button (click)="snapScroller.next()">N</button>
      </div>

    </ng-container>-->
    <ng-container *ngrxLet="children$; let children">
      <ng-container *ngrxLet="isFreeGrid$; let isFreeGrid">
        <ng-container *ngIf="isFreeGrid">
          <div [nxMassaudResponsiveClasses]="(classNames$ | ngrxPush) ?? []">
            <ng-template
              ngFor
              [ngForOf]="children"
              let-child
              let-index="index"
              [ngForTrackBy]="trackByItemId"
            >
              <!--<ng-container *ngIf="index===0">
                <div class="layout-paragraph">
                  <ng-template *milParagraphSwitcher="paragraph"></ng-template>
                </div>
              </ng-container>-->
              <ng-template *milParagraphSwitcher="$any(child)"></ng-template>
              <ng-template *nxMassaudMediaSwitcher="$any(child)"></ng-template>
            </ng-template>
          </div>
        </ng-container>
        <ng-container *ngIf="!isFreeGrid">
          <ul [nxMassaudResponsiveClasses]="(classNames$ | ngrxPush) ?? []">
            <ng-template
              ngFor
              [ngForOf]="children"
              let-child
              let-index="index"
              [ngForTrackBy]="trackByItemId"
            >
              <!--<ng-container *ngIf="index===0">
                <div class="layout-paragraph">
                  <ng-template *milParagraphSwitcher="paragraph"></ng-template>
                </div>
              </ng-container>-->
              <li
                [class]="child.type"
                [nxMassaudResponsiveClasses]="
                  (itemClassNames$ | ngrxPush) ?? []
                "
              >
                <ng-template *milParagraphSwitcher="$any(child)"></ng-template>
                <ng-template
                  *nxMassaudMediaSwitcher="$any(child)"
                ></ng-template>
              </li>
            </ng-template>
          </ul>
        </ng-container>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./layout.component.scss', './layout-contact.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class LayoutComponent
  extends ComponentStore<LayoutComponentState>
  implements ParagraphComponent
{
  private el: HTMLElement = inject(ElementRef<HTMLElement>).nativeElement;

  readonly children$ = this.select((s) => s.children);
  readonly itemClassNames$ = this.select((s) => s.itemClassNames);
  readonly classNames$ = this.select((s) => s.classNames);
  readonly isFreeGrid$ = this.classNames$.pipe(
    map((classNames) => classNames?.indexOf('free-grid') > -1)
  );

  trackByItemId = trackByProp<DrupalIdentifier>('id');

  @Input()
  set paragraph(
    paragraph: ParagraphLayout | ParagraphMediaGallery | undefined
  ) {
    if (paragraph) {
      this.patchState({
        classNames: paragraph.classNames,
        itemClassNames: paragraph.itemClassNames,
      });
      if (paragraph.classNames) {
        paragraph.classNames
          .filter((c) => c.startsWith('host-'))
          .forEach((cls) => {
            this.el.classList.add(cls.split('host-')[1]);
          });
      }
    }
    if (paragraph?.type === ParagraphLayout.type) {
      this.patchState({
        children: (paragraph as ParagraphLayout).paragraphs ?? [],
      });
    } else if (paragraph?.type === ParagraphMediaGallery.type) {
      this.patchState({
        children: (paragraph as ParagraphMediaGallery).media ?? [],
      });
    }
  }

  @Input()
  set items(items: (MilParagraph | Media)[]) {
    this.patchState({ children: items });
  }

  constructor() {
    super({ children: [], itemClassNames: [], classNames: [] });
  }
}
